import { Link } from 'react-router-dom';
import { Flag } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDashboardAnalytics } from '@/hooks/useDashboardAnalytics';
import { DashboardSkeleton } from '@/components/analytics/DashboardSkeleton';
import { ErrorState } from '@/components/scenarios/ErrorState';

export default function FlagsPage() {
  const { data, loading, error, refetch } = useDashboardAnalytics();
  const archive = data?.scenario?.archive;
  const ready = data?.scenario?.ready;

  if (loading) return <DashboardSkeleton />;
  if (error || !data) {
    return (
      <ErrorState
        error={error || 'Failed to load flags data'}
        onRetry={refetch}
      />
    );
  }

  const totalCtf = archive?.total_ctf_scenario ?? 0;
  const readyCtf = ready?.total_ctf_scenario ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
          Flags (CTF)
        </h1>
        <p className="text-muted-foreground mt-1">
          Capture the Flag scenarios and flag-related metrics
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-primary" />
              Total CTF Scenarios
            </CardTitle>
            <CardDescription>
              Archived CTF scenarios available on the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-primary">{totalCtf}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-primary" />
              Ready CTF Scenarios
            </CardTitle>
            <CardDescription>
              CTF scenarios ready to run
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-primary">{readyCtf}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Scenario libraries</CardTitle>
          <CardDescription>
            Browse corporate and CTF scenarios that include flags and challenges
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button asChild variant="default">
            <Link to="/scenarios">Scenarios</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/scenarios/actives">Active Scenarios</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
